// Stars Parallax Animation
// Adapted to work within React card components with proper scoping

const STAR_COLOR = '#fff';
const STAR_SIZE = 3;
const STAR_MIN_SCALE = 0.2;
const OVERFLOW_THRESHOLD = 50;

class StarsParallax {
  constructor(containerElement) {
    this.container = containerElement;
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d');
    
    this.scale = 1;
    this.width = 0;
    this.height = 0;
    this.stars = [];
    this.pointerX = null;
    this.pointerY = null;
    this.velocity = { x: 0, y: 0, tx: 0, ty: 0, z: 0.0005 };
    this.touchInput = false;
    this.destroyed = false;
    this.animationId = null;
    
    // Calculate star count based on container size
    this.STAR_COUNT = 0;
    
    this.init();
  }
  
  init() {
    // Style the canvas
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '1';
    
    this.container.appendChild(this.canvas);
    
    // Bind event handlers
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    
    // Add event listeners to container
    this.container.addEventListener('mousemove', this.onMouseMove);
    this.container.addEventListener('touchmove', this.onTouchMove);
    this.container.addEventListener('touchend', this.onMouseLeave);
    this.container.addEventListener('mouseleave', this.onMouseLeave);
    
    this.resize();
    this.generate();
    this.step();
  }
  
  generate() {
    this.stars = [];
    for (let i = 0; i < this.STAR_COUNT; i++) {
      this.stars.push({
        x: 0,
        y: 0,
        z: STAR_MIN_SCALE + Math.random() * (1 - STAR_MIN_SCALE)
      });
    }
    this.stars.forEach(star => this.placeStar(star));
  }
  
  placeStar(star) {
    star.x = Math.random() * this.width;
    star.y = Math.random() * this.height;
  }
  
  recycleStar(star) {
    let direction = 'z';
    
    let vx = Math.abs(this.velocity.x);
    let vy = Math.abs(this.velocity.y);
    
    if (vx > 1 || vy > 1) {
      let axis;
      
      if (vx > vy) {
        axis = Math.random() < vx / (vx + vy) ? 'h' : 'v';
      } else {
        axis = Math.random() < vy / (vx + vy) ? 'v' : 'h';
      }
      
      if (axis === 'h') {
        direction = this.velocity.x > 0 ? 'l' : 'r';
      } else {
        direction = this.velocity.y > 0 ? 't' : 'b';
      }
    }
    
    star.z = STAR_MIN_SCALE + Math.random() * (1 - STAR_MIN_SCALE);
    
    if (direction === 'z') {
      star.z = 0.1;
      star.x = Math.random() * this.width;
      star.y = Math.random() * this.height;
    } else if (direction === 'l') {
      star.x = -OVERFLOW_THRESHOLD;
      star.y = this.height * Math.random();
    } else if (direction === 'r') {
      star.x = this.width + OVERFLOW_THRESHOLD;
      star.y = this.height * Math.random();
    } else if (direction === 't') {
      star.x = this.width * Math.random();
      star.y = -OVERFLOW_THRESHOLD;
    } else if (direction === 'b') {
      star.x = this.width * Math.random();
      star.y = this.height + OVERFLOW_THRESHOLD;
    }
  }
  
  resize() {
    const rect = this.container.getBoundingClientRect();
    this.scale = window.devicePixelRatio || 1;
    
    this.width = rect.width * this.scale;
    this.height = rect.height * this.scale;
    
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    
    // Recalculate star count based on container size
    this.STAR_COUNT = Math.floor((rect.width + rect.height) / 8);
    
    if (this.stars.length === 0) {
      return;
    }
    
    // If star count changed significantly, regenerate
    if (Math.abs(this.stars.length - this.STAR_COUNT) > 20) {
      this.generate();
    } else {
      this.stars.forEach(star => this.placeStar(star));
    }
  }
  
  step() {
    if (this.destroyed) return;
    
    this.context.clearRect(0, 0, this.width, this.height);
    this.update();
    this.render();
    
    this.animationId = requestAnimationFrame(() => this.step());
  }
  
  update() {
    this.velocity.tx *= 0.96;
    this.velocity.ty *= 0.96;
    
    this.velocity.x += (this.velocity.tx - this.velocity.x) * 0.8;
    this.velocity.y += (this.velocity.ty - this.velocity.y) * 0.8;
    
    this.stars.forEach((star) => {
      star.x += this.velocity.x * star.z;
      star.y += this.velocity.y * star.z;
      
      star.x += (star.x - this.width / 2) * this.velocity.z * star.z;
      star.y += (star.y - this.height / 2) * this.velocity.z * star.z;
      star.z += this.velocity.z;
      
      // recycle when out of bounds
      if (star.x < -OVERFLOW_THRESHOLD || star.x > this.width + OVERFLOW_THRESHOLD ||
          star.y < -OVERFLOW_THRESHOLD || star.y > this.height + OVERFLOW_THRESHOLD) {
        this.recycleStar(star);
      }
    });
  }
  
  render() {
    this.stars.forEach((star) => {
      this.context.beginPath();
      this.context.lineCap = 'round';
      this.context.lineWidth = STAR_SIZE * star.z * this.scale;
      this.context.globalAlpha = 0.5 + 0.5 * Math.random();
      this.context.strokeStyle = STAR_COLOR;
      
      this.context.beginPath();
      this.context.moveTo(star.x, star.y);
      
      let tailX = this.velocity.x * 2;
      let tailY = this.velocity.y * 2;
      
      // stroke() won't work on an invisible line
      if (Math.abs(tailX) < 0.1) tailX = 0.5;
      if (Math.abs(tailY) < 0.1) tailY = 0.5;
      
      this.context.lineTo(star.x + tailX, star.y + tailY);
      this.context.stroke();
    });
  }
  
  movePointer(x, y) {
    if (typeof this.pointerX === 'number' && typeof this.pointerY === 'number') {
      let ox = x - this.pointerX;
      let oy = y - this.pointerY;
      
      this.velocity.tx = this.velocity.tx + (ox / 8 * this.scale) * (this.touchInput ? 1 : -1);
      this.velocity.ty = this.velocity.ty + (oy / 8 * this.scale) * (this.touchInput ? 1 : -1);
    }
    
    this.pointerX = x;
    this.pointerY = y;
  }
  
  onMouseMove(event) {
    this.touchInput = false;
    const rect = this.container.getBoundingClientRect();
    this.movePointer(event.clientX - rect.left, event.clientY - rect.top);
  }
  
  onTouchMove(event) {
    this.touchInput = true;
    const rect = this.container.getBoundingClientRect();
    this.movePointer(
      event.touches[0].clientX - rect.left,
      event.touches[0].clientY - rect.top
    );
    event.preventDefault();
  }
  
  onMouseLeave() {
    this.pointerX = null;
    this.pointerY = null;
  }
  
  destroy() {
    this.destroyed = true;
    
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    
    // Remove event listeners
    this.container.removeEventListener('mousemove', this.onMouseMove);
    this.container.removeEventListener('touchmove', this.onTouchMove);
    this.container.removeEventListener('touchend', this.onMouseLeave);
    this.container.removeEventListener('mouseleave', this.onMouseLeave);
    
    // Remove canvas
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

// Initialize Stars Parallax background for a container element
export function initStarsParallax(containerElement) {
  if (!containerElement) return null;
  
  // Make sure the container has position relative for absolute positioning of canvas
  const computedStyle = window.getComputedStyle(containerElement);
  if (computedStyle.position === 'static') {
    containerElement.style.position = 'relative';
  }
  
  return new StarsParallax(containerElement);
}

// Cleanup function to destroy Stars Parallax instance
export function destroyStarsParallax(instance) {
  if (instance && instance.destroy) {
    instance.destroy();
  }
}
